import { h, Fragment } from "preact";
import { useMultipleSelection, useSelect } from "downshift";

import { addHighlightSpans, classNames, renderHtml } from "../helpers.js";

import Checkbox from "./Checkbox.jsx";
import { useEffect, useState } from "preact/hooks";
import TextField from "./TextField.jsx";
import Icon from "./Icon.jsx";

export default function NioDropdown({
  label,
  items,
  trigger,
  onSelect,
  withFilter,
  placeholder,
  onFilterChange,
  useOwnFilter,
  closeOnSelect,
  singleSelect,
  ...props
}) {
  const [filterValue, setFilterValue] = useState("");
  const { getDropdownProps, addSelectedItem } = useMultipleSelection({
    initialSelectedItems: items.filter((x) => x.selected),
  });

  let possibleItems = items;
  if (!useOwnFilter && withFilter && filterValue.length > 0) {
    possibleItems = possibleItems.filter((item) =>
      item.filter
        ? item.filter(filterValue)
        : item.value.toLowerCase().indexOf(filterValue) > -1
    );
  }

  const {
    isOpen,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    highlightedIndex,
    getItemProps,
  } = useSelect({
    selectedItem: null,
    defaultHighlightedIndex: 0, // after selection, highlight the first item.
    items: possibleItems,
    stateReducer: (state, actionAndChanges) => {
      const { changes, type } = actionAndChanges;
      switch (type) {
        case useSelect.stateChangeTypes.ToggleButtonKeyDownEnter:
        case useSelect.stateChangeTypes.ToggleButtonKeyDownSpaceButton:
        case useSelect.stateChangeTypes.ItemClick:
          return {
            ...changes,
            highlightedIndex: state.highlightedIndex, // Do not reset highlighted index
            isOpen: closeOnSelect ? false : state.isOpen, // keep the menu open after selection.
          };
      }
      return changes;
    },
    onStateChange: ({ type, selectedItem }) => {
      switch (type) {
        case useSelect.stateChangeTypes.ToggleKeyDownEnter:
        case useSelect.stateChangeTypes.ToggleKeyDownSpaceButton:
        case useSelect.stateChangeTypes.ItemClick:
          if (selectedItem) {
            addSelectedItem(selectedItem);
            onSelect(selectedItem);
          }
          break;
        default:
          break;
      }
    },
  });

  function handleFilterChange(event) {
    const value = event.target.value.toLowerCase();
    setFilterValue(value);
  }

  function handleFilterUp(event) {
    if (event.which === 13 || event.which === 38 || event.which === 40) {
      return;
    }
    event.stopPropagation();
  }

  useEffect(() => {
    onFilterChange && onFilterChange(filterValue);
  }, [filterValue]);

  return (
    <div className={classNames("nio-dropdown", props.class)}>
      <div className="nio-dropdown__triggerWrap">
        {label && label(getLabelProps)}
        <div className="nio-dropdown__trigger">
          {trigger((props = {}) => ({
            ...props,
            ...getToggleButtonProps(
              getDropdownProps({ preventKeyAction: isOpen })
            ),
          }))}
        </div>
      </div>
      <div
        {...getMenuProps()}
        className={classNames(
          "nio-dropdown__dropdownWrap",
          withFilter && "nio-dropdown__dropdownWrap--withFilter"
        )}
      >
        {isOpen && (
          <Fragment>
            {withFilter && (
              <div class="nio-dropdown__dropdownFilter">
                <TextField
                  slim
                  type="text"
                  placeholder={placeholder}
                  onInput={handleFilterChange}
                  value={filterValue}
                  onKeyDown={handleFilterUp}
                  class="mb-0"
                />
                <Icon
                  class={classNames(
                    "nio-dropdown__dropdownFilterCloser",
                    filterValue && "nio-dropdown__dropdownFilterCloser--visible"
                  )}
                  onClick={() => setFilterValue("")}
                >
                  close
                </Icon>
              </div>
            )}
            <ul className="nio-dropdown__dropdown">
              {possibleItems.map((item, index) => (
                <li
                  className={classNames(
                    "nio-dropdown__dropdownItem",
                    highlightedIndex === index &&
                      "nio-dropdown__dropdownItem--highlighted",
                    item.selected && "nio-dropdown__dropdownItem--selected",
                    withFilter && "nio-dropdown__dropdownItem--withFilter"
                  )}
                  key={`${item.value}${index}`}
                  {...getItemProps({ item, index })}
                  onClick={(e) => {
                    // Somehow doing this prevents sometimes occuring double click issue
                    e.stopPropagation();
                    getItemProps({ item, index }).onClick(e);
                  }}
                >
                  {singleSelect ? (
                    <div class="nio-dropdown__dropdownItemContent">
                      {item.content && typeof item.content === "function"
                        ? item.content(filterValue)
                        : renderHtml(
                            addHighlightSpans(
                              item.content || item.text,
                              filterValue
                            )
                          )}
                    </div>
                  ) : (
                    <Checkbox
                      inline
                      checked={item.selected}
                      key={item.value}
                      count={item.count}
                      description={item.description}
                      {...getItemProps({ item, index })}
                    >
                      {item.content && typeof item.content === "function"
                        ? item.content(filterValue)
                        : renderHtml(
                            addHighlightSpans(
                              item.content || item.text,
                              filterValue
                            )
                          )}
                    </Checkbox>
                  )}
                </li>
              ))}
            </ul>
          </Fragment>
        )}
      </div>
    </div>
  );
}
